import React from "react";
import { useRouteError } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import { ThemeProvider } from "@emotion/react";
import theme from "themes/v2/AppTheme";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import LogoutIcon from "@mui/icons-material/Logout";
import HomeIcon from "@mui/icons-material/Home";

const PageWrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
  padding: "20px",
  position: "relative",
});

const TopActions = styled(Box)({
  position: "fixed",
  top: 20,
  right: 20,
  display: "flex",
  gap: "0.5rem",
  zIndex: 10,
});

const ContentCard = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  border: "solid 2px #2EA9C7",
  borderRadius: "10px",
  padding: "4vh 8vh",
  boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
  maxWidth: "650px",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    padding: "3vh 4vh",
    maxWidth: "90%",
  },
}));

const ErrorPage = () => {
  const error = useRouteError();
  const { logout } = useAuth0();
  // Quand ErrorPage est rendue via la route catch-all `path: '*'` (URL inconnue),
  // ce n'est pas une véritable erreur de routage : useRouteError() renvoie undefined.
  // On considère donc l'absence d'erreur (ou un statut 404 explicite) comme "page introuvable".
  const isNotFound = !error || error?.status === 404;

  return (
    <ThemeProvider theme={theme}>
      <PageWrapper>
        <TopActions>
          <Tooltip title="Se déconnecter" arrow>
            <Button
              onClick={() => logout({ logoutParams: { returnTo: `${window.location.origin}/auth` } })}
              startIcon={<LogoutIcon />}
              sx={{
                backgroundColor: "#fff",
                color: "#2EA9C7",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#2EA9C7", color: "#fff" },
              }}
            >
              Déconnexion
            </Button>
          </Tooltip>
        </TopActions>

        <ContentCard>
          {isNotFound ? (
            <SearchOffIcon sx={{ fontSize: "4rem", color: "#2EA9C7", mb: 2, filter: "drop-shadow(0 4px 8px rgba(46,169,199,0.3))" }} />
          ) : (
            <ReportProblemIcon sx={{ fontSize: "4rem", color: "#fb544e", mb: 2, filter: "drop-shadow(0 4px 8px rgba(251,84,78,0.3))" }} />
          )}

          <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: "#2c2c2c" }}>
            {isNotFound ? "Page introuvable" : "Oups !"}
          </Typography>

          <Typography variant="body1" sx={{ fontSize: "0.95rem", lineHeight: 1.5, mb: 1, color: "#2c2c2c" }}>
            {isNotFound
              ? "Cette page n'existe pas ou a été déplacée. Vérifiez l'adresse ou retournez à l'accueil."
              : "Nous sommes désolés, une erreur inattendue est survenue."}
          </Typography>

          {error && !isNotFound && (
            <Typography variant="body2" sx={{ mt: 1, mb: 1, color: "#838383", fontStyle: "italic" }}>
              {error.statusText || error.message}
            </Typography>
          )}

          <Button
            href="/auth"
            variant="contained"
            startIcon={<HomeIcon />}
            sx={{
              mt: 2,
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#2EA9C7",
              "&:hover": { backgroundColor: "#004d68" },
            }}
          >
            Retour à l'accueil
          </Button>
        </ContentCard>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default ErrorPage;
